import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD,
    GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD_FOR_DRIVER
} from '../queries/getDailyTaskDetailQueries';
import {
    GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD_FOR_DSP,
    GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD_FOR_DRIVER_FOR_DSP
} from '../queries/getDspDailyTaskDetailQueries';

export type DailyTaskDetailMode = 'dsp' | 'whs';

export interface GraphQLDetailData {
    id: number;
    taskDate: string;
    warehouseId: number;
    dspId: number;
    dspName?: string;
    areaId: number;
    areaName?: string;
    driverId: number;
    deliveryDriverId?: number;
    driverName?: string;
    trackingNumber: string;
    orderId: number;
    currentStatus?: number;
    overallStatusText?: string;
    lastFailureReason?: string;
    exceptionReasonCode?: string;
    latestPhysicalTrackingUpdatedAt?: string;
}

export interface DailyTaskDetailPageRequest {
    page: number;
    size: number;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
}

export interface UseDailyTaskDetailQueryResult {
    detailData: GraphQLDetailData[];
    /** 最近一次成功请求的 requestId，用于忽略过期响应，避免竞态导致列表显示错误条数 */
    detailDataRequestId: number | null;
    pagination: {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
        hasPrevious: boolean;
    } | null;
    loading: boolean;
    error: any;
    fetchDetailsByStatusField: (taskDate: string, statusFields: string[] | undefined, pageRequest: DailyTaskDetailPageRequest, warehouseId?: string | number, warehouseIds?: number[], hubId?: string | number, dspIds?: number[], areaIds?: number[], driverIds?: number[], cities?: string[], requestId?: number) => Promise<void>;
    fetchDetailsByStatusFieldForDriver: (taskDate: string, statusFields: string[] | undefined, pageRequest: DailyTaskDetailPageRequest, warehouseId?: string | number, warehouseIds?: number[], dspIds?: number[], areaIds?: number[], driverIds?: number[], cities?: string[], requestId?: number) => Promise<void>;
}

export const useDailyTaskDetailQuery = (mode: DailyTaskDetailMode): UseDailyTaskDetailQueryResult => {
    const isDsp = mode === 'dsp';

    const client = useApolloClient();
    const [detailData, setDetailData] = useState<GraphQLDetailData[]>([]);
    const [detailDataRequestId, setDetailDataRequestId] = useState<number | null>(null);
    const [pagination, setPagination] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);

    const fetchDetailsByStatusField = async (
        taskDate: string,
        statusFields: string[] | undefined,
        pageRequest: DailyTaskDetailPageRequest,
        warehouseId?: string | number,
        warehouseIds?: number[],
        hubId?: string | number,
        dspIds?: number[],
        areaIds?: number[],
        driverIds?: number[],
        cities?: string[],
        requestId?: number
    ) => {
        setLoading(true);
        setError(null);
        try {
            // warehouseIds 优先：多选 WHS 时传数组；否则传单个 warehouseId
            const warehouseIdNum = (warehouseIds?.length ? undefined : (typeof warehouseId === 'undefined'
                ? undefined
                : (typeof warehouseId === 'string' ? parseInt(warehouseId, 10) : Number(warehouseId))));
            if (typeof warehouseIdNum !== 'undefined' && (!warehouseIdNum || isNaN(warehouseIdNum))) {
                throw new Error('warehouseId must be a valid number when provided');
            }

            const hubIdNum = typeof hubId === 'undefined'
                ? undefined
                : (typeof hubId === 'string' ? parseInt(hubId, 10) : Number(hubId));
            if (typeof hubIdNum !== 'undefined' && (!hubIdNum || isNaN(hubIdNum))) {
                throw new Error('hubId must be a valid number when provided');
            }

            const { data } = await client.query({
                query: isDsp ? GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD_FOR_DSP : GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD,
                variables: {
                    taskDate,
                    statusFields,
                    pageRequest,
                    warehouseId: warehouseIdNum,
                    warehouseIds: warehouseIds?.length ? warehouseIds : undefined,
                    hubId: hubIdNum,
                    dspIds,
                    areaIds,
                    driverIds,
                    cityNames: cities
                }
            });

            const result = isDsp ? data.getDetailsByStatusFieldForDsp : data.getDetailsByStatusField;
            if (result) {
                setDetailData(result.content || []);
                setDetailDataRequestId(requestId ?? null);
                setPagination({
                    page: result.page,
                    size: result.size,
                    totalElements: result.totalElements,
                    totalPages: result.totalPages,
                    hasNext: result.hasNext,
                    hasPrevious: result.hasPrevious
                });
            }
        } catch (err) {
            setError(err);
            console.error('获取状态字段详情数据失败:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchDetailsByStatusFieldForDriver = async (
        taskDate: string,
        statusFields: string[] | undefined,
        pageRequest: DailyTaskDetailPageRequest,
        warehouseId?: string | number,
        warehouseIds?: number[],
        dspIds?: number[],
        areaIds?: number[],
        driverIds?: number[],
        cities?: string[],
        requestId?: number
    ) => {
        setLoading(true);
        setError(null);
        try {
            const warehouseIdNum = (warehouseIds?.length ? undefined : (typeof warehouseId === 'undefined'
                ? undefined
                : (typeof warehouseId === 'string' ? parseInt(warehouseId, 10) : Number(warehouseId))));
            if (typeof warehouseIdNum !== 'undefined' && (!warehouseIdNum || isNaN(warehouseIdNum))) {
                throw new Error('warehouseId must be a valid number when provided');
            }

            const { data } = await client.query({
                query: isDsp ? GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD_FOR_DRIVER_FOR_DSP : GET_DAILY_TASK_DETAIL_BY_STATUS_FIELD_FOR_DRIVER,
                variables: {
                    taskDate,
                    statusFields,
                    pageRequest,
                    warehouseId: warehouseIdNum,
                    warehouseIds: warehouseIds?.length ? warehouseIds : undefined,
                    dspIds,
                    areaIds,
                    driverIds,
                    cityNames: cities
                }
            });

            const result = isDsp ? data.getDetailsByStatusFieldForDriverForDsp : data.getDetailsByStatusFieldForDriver;
            if (result) {
                setDetailData(result.content || []);
                setDetailDataRequestId(requestId ?? null);
                setPagination({
                    page: result.page,
                    size: result.size,
                    totalElements: result.totalElements,
                    totalPages: result.totalPages,
                    hasNext: result.hasNext,
                    hasPrevious: result.hasPrevious
                });
            }
        } catch (err) {
            setError(err);
            console.error('获取司机维度状态字段详情数据失败:', err);
        } finally {
            setLoading(false);
        }
    };

    return {
        detailData,
        detailDataRequestId,
        pagination,
        loading,
        error,
        fetchDetailsByStatusField,
        fetchDetailsByStatusFieldForDriver
    };
};
